import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { ArrowRight, CheckCircle, Target, Users, Settings, Building2, Shield, Heart, GraduationCap, Rocket } from 'lucide-react';
import { motion } from 'framer-motion';

export const AcceleratorPage = () => {
  const pillars = [
    { icon: Users, name: 'Executive Leadership', desc: 'Superintendent coaching, cabinet development, leadership pipeline' },
    { icon: Target, name: 'District Systems', desc: 'Strategic planning, accountability, dashboards, operational reviews' },
    { icon: Building2, name: 'School Performance', desc: 'School improvement, MTSS, walkthroughs, ACT preparation' },
    { icon: Settings, name: 'Workforce & Innovation', desc: 'CTE development, industry partnerships, Perkins, career academies' },
    { icon: Shield, name: 'Governance', desc: 'Board development, policy implementation, strategic planning' },
    { icon: Heart, name: 'Safety & Culture', desc: 'De-escalation, crisis leadership, professional conduct, culture' },
    { icon: GraduationCap, name: 'Legacy', desc: 'Communication, community partnerships, transition planning' },
  ];

  const phases = [
    {
      phase: 'Phase 1',
      title: 'Discover',
      duration: 'Month 1-2',
      items: ['Comprehensive district assessment', 'Stakeholder interviews', 'Data analysis', 'Current state documentation'],
    },
    {
      phase: 'Phase 2',
      title: 'Design',
      duration: 'Month 2-3',
      items: ['Strategic roadmap development', 'Priority identification', 'Quick wins identification', 'Resource allocation planning'],
    },
    {
      phase: 'Phase 3',
      title: 'Develop',
      duration: 'Month 3-9',
      items: ['Leadership capacity building', 'Systems implementation', 'Professional development', 'Coaching and mentoring'],
    },
    {
      phase: 'Phase 4',
      title: 'Deploy',
      duration: 'Month 6-12',
      items: ['Full implementation support', 'Change management', 'Progress monitoring', 'Course corrections'],
    },
    {
      phase: 'Phase 5',
      title: 'Demonstrate',
      duration: 'Ongoing',
      items: ['Results measurement', 'Impact documentation', 'Sustainability planning', 'Legacy development'],
    },
  ];

  const outcomes = [
    'Aligned executive cabinet with shared vision and accountability',
    'Comprehensive strategic plan with clear implementation roadmap',
    'Strengthened board-superintendent governance relationship',
    '100% College & Career Readiness systems and monitoring',
    'Improved school performance metrics and student outcomes',
    'Sustainable systems that continue long after engagement ends',
  ];

  return (
    <div className="pt-20 lg:pt-24" data-testid="accelerator-page">
      {/* Hero Section */}
      <section className="relative min-h-[70vh] flex items-center" data-testid="accelerator-hero">
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: 'url(https://images.unsplash.com/photo-1521737604893-d14cc237f11d?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODl8MHwxfHNlYXJjaHw0fHxwcm9mZXNzaW9uYWwlMjBkZXZlbG9wbWVudCUyMHdvcmtzaG9wJTIwdGVhbSUyMGNvbGxhYm9yYXRpb258ZW58MHx8fHwxNzgzODY4ODk0fDA&ixlib=rb-4.1.0&q=85)',
          }}
        />
        <div className="absolute inset-0 hero-gradient" />
        
        <div className="container-premium relative z-10 py-20">
          <div className="max-w-3xl">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="inline-flex items-center gap-2 bg-[#4F8A2F] text-[#0B1F3A] px-4 py-2 rounded-full mb-6"
            >
              <Rocket className="w-4 h-4" />
              <span className="text-sm font-semibold">Flagship Program</span>
            </motion.div>
            
            <motion.h1 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold text-white leading-tight mb-6"
            >
              Legacy District Impact Accelerator™
            </motion.h1>
            
            <motion.p 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="text-slate-200 text-lg lg:text-xl leading-relaxed mb-10"
            >
              A comprehensive district transformation partnership that integrates all seven pillars of the Legacy Leadership System into a cohesive, measurable, and sustainable change initiative.
            </motion.p>
            
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="flex flex-col sm:flex-row gap-4"
            >
              <Link to="/contact">
                <Button className="bg-[#4F8A2F] hover:bg-[#6FAE3A] text-[#0B1F3A] rounded-full px-8 py-5 font-body font-semibold text-base">
                  Schedule a Strategy Session
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </Link>
              <Button variant="outline" className="border-2 border-white/40 text-white hover:bg-white/10 rounded-full px-8 py-5 font-body text-base">
                Download Program Overview
              </Button>
            </motion.div>
          </div>
        </div>
      </section>

      {/* What's Included - 7 Pillars */}
      <section className="section-padding bg-white" data-testid="pillars-section">
        <div className="container-premium">
          <div className="max-w-2xl mb-16">
            <p className="overline mb-4">Comprehensive Framework</p>
            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold text-[#172033] mb-6">
              Seven Integrated Pillars
            </h2>
            <p className="text-slate-600 text-lg leading-relaxed">
              The Accelerator addresses every dimension of district performance through our proven Legacy Leadership System.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {pillars.map((pillar, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: index * 0.05 }}
                className="card-premium hover-lift"
              >
                <div className="w-12 h-12 bg-[#0B1F3A]/10 rounded-xl flex items-center justify-center mb-4">
                  <pillar.icon className="w-6 h-6 text-[#0B1F3A]" strokeWidth={1.5} />
                </div>
                <h3 className="font-display text-lg font-bold text-[#172033] mb-2">{pillar.name}</h3>
                <p className="text-slate-600 text-sm leading-relaxed">{pillar.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Implementation Timeline */}
      <section className="section-padding bg-[#F7F8F6]" data-testid="timeline-section">
        <div className="container-premium">
          <div className="text-center mb-16">
            <p className="overline mb-4">Implementation Approach</p>
            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold text-[#172033] mb-6">
              Five-Phase Transformation
            </h2>
            <p className="text-slate-600 text-lg max-w-2xl mx-auto">
              A structured approach that ensures sustainable change through discovery, design, development, deployment, and demonstration.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-5 gap-6">
            {phases.map((phase, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: index * 0.1 }}
                className="bg-white rounded-2xl p-6 border border-slate-100"
              >
                <div className="flex items-center gap-3 mb-4">
                  <span className="w-10 h-10 bg-[#0B1F3A] text-white rounded-xl flex items-center justify-center font-display font-bold text-sm">
                    {index + 1}
                  </span>
                  <div>
                    <p className="text-xs text-[#4F8A2F] font-semibold">{phase.phase}</p>
                    <h3 className="font-display text-lg font-bold text-[#172033]">{phase.title}</h3>
                  </div>
                </div>
                <p className="text-xs text-slate-500 mb-3">{phase.duration}</p>
                <ul className="space-y-2">
                  {phase.items.map((item, i) => (
                    <li key={i} className="text-sm text-slate-600 flex items-start gap-2">
                      <span className="w-1 h-1 bg-[#4F8A2F] rounded-full mt-2 flex-shrink-0" />
                      {item}
                    </li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Expected Outcomes */}
      <section className="section-padding bg-[#0B1F3A]" data-testid="outcomes-section">
        <div className="container-premium">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            <div>
              <p className="overline text-[#4F8A2F] mb-4">Expected Results</p>
              <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-6">
                Outcomes That Last
              </h2>
              <p className="text-slate-300 text-lg leading-relaxed mb-8">
                The Accelerator is designed to produce measurable results that continue long after the consulting engagement ends.
              </p>
              <ul className="space-y-4">
                {outcomes.map((outcome, index) => (
                  <motion.li
                    key={index}
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.4, delay: index * 0.1 }}
                    className="flex items-start gap-3"
                  >
                    <CheckCircle className="w-5 h-5 text-[#4F8A2F] mt-1 flex-shrink-0" strokeWidth={2} />
                    <span className="text-slate-200">{outcome}</span>
                  </motion.li>
                ))}
              </ul>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="bg-white/10 backdrop-blur p-8 rounded-2xl text-center">
                <p className="font-display text-4xl lg:text-5xl font-bold text-[#4F8A2F] mb-2">12+</p>
                <p className="text-sm text-slate-300">Month Partnership</p>
              </div>
              <div className="bg-white/10 backdrop-blur p-8 rounded-2xl text-center">
                <p className="font-display text-4xl lg:text-5xl font-bold text-[#4F8A2F] mb-2">7</p>
                <p className="text-sm text-slate-300">Integrated Pillars</p>
              </div>
              <div className="bg-white/10 backdrop-blur p-8 rounded-2xl text-center">
                <p className="font-display text-4xl lg:text-5xl font-bold text-[#4F8A2F] mb-2">5</p>
                <p className="text-sm text-slate-300">Phase Approach</p>
              </div>
              <div className="bg-white/10 backdrop-blur p-8 rounded-2xl text-center">
                <p className="font-display text-4xl lg:text-5xl font-bold text-[#4F8A2F] mb-2">∞</p>
                <p className="text-sm text-slate-300">Lasting Results</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section-padding bg-white">
        <div className="container-premium text-center">
          <p className="overline mb-4">Ready to Accelerate?</p>
          <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold text-[#172033] mb-6">
            Transform Your District
          </h2>
          <p className="text-slate-600 text-lg max-w-2xl mx-auto mb-10">
            Schedule a confidential strategy session to discuss how the Legacy District Impact Accelerator can partner with your district to produce results that last.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/contact">
              <Button className="bg-[#0B1F3A] hover:bg-[#172033] text-white rounded-full px-10 py-5 font-body text-lg">
                Schedule a Strategy Session
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>
            <Button variant="outline" className="border-2 border-[#0B1F3A] text-[#0B1F3A] hover:bg-[#0B1F3A] hover:text-white rounded-full px-10 py-5 font-body text-lg">
              Download Program Overview
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
};
